import createClient, { type Client, type Middleware } from 'openapi-fetch'

import { ApiError } from './errors'
import { networkErrorMiddleware } from './middleware/errors'
import { backendUrl } from '@/shared/config/runtime'
import type { paths } from './generated/schema'

/**
 * Builds a typed client against the configured backend.
 *
 * `baseUrl` is the bare origin: every path in the generated schema already carries
 * its own `/api` prefix, so appending one here produces `/api/api/...`.
 */
export function createApiClient(middleware: Middleware[] = []): Client<paths> {
  const client = createClient<paths>({ baseUrl: backendUrl() })
  if (middleware.length > 0) {
    client.use(...middleware)
  }
  return client
}

let bare: Client<paths> | null = null

/**
 * A client with no session handling.
 *
 * The refresh endpoint has to be reachable without the refresh middleware, or a
 * refresh triggered by an expiring token would trigger a refresh of its own. It is
 * also the right client for the genuinely unauthenticated endpoints, which have no
 * use for an `Authorization` header.
 *
 * It still maps a failed connection, because that failure means the same thing on
 * every client and the sign-in page reads it off this one.
 */
export function getBareApi(): Client<paths> {
  bare ??= createApiClient([networkErrorMiddleware])
  return bare
}

/**
 * Turns an openapi-fetch result into the response body, or throws `ApiError`.
 *
 * Every call site needs this, and doing it by hand invites the failure mode it
 * closes over: throwing the raw error body, which is not an `Error` and so
 * arrives at the query cache and the toaster without a stack or a message.
 *
 * Success is decided by the response, not by whether a body came back. Reading
 * an absent body as failure would be wrong for every endpoint that answers 204 --
 * deleting an application, leaving a workspace, and both reorder endpoints all
 * do -- and the failure is not a visible one: the mutation rejects, the
 * optimistic update rolls back, and the change appears to undo itself a moment
 * after it worked.
 */
export async function unwrap<T>(
  result: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await result
  if (error !== undefined || !response.ok) {
    throw new ApiError(response.status, error)
  }
  return data as T
}

export function __resetBareApiForTests(): void {
  bare = null
}
